/**
 * Currency utility functions
 */
import axios from '../lib/axios';

export interface CurrencyInfo {
    symbol: string;
    code: string;
    name: string;
}

/**
 * Mapping of currency codes to their symbols and names
 */
export const CURRENCY_MAP: Record<string, CurrencyInfo> = {
    USD: { symbol: '$', code: 'USD', name: 'US Dollar' },
    EUR: { symbol: '€', code: 'EUR', name: 'Euro' },
    GBP: { symbol: '£', code: 'GBP', name: 'British Pound' },
    JPY: { symbol: '¥', code: 'JPY', name: 'Japanese Yen' },
    CNY: { symbol: '¥', code: 'CNY', name: 'Chinese Yuan' },
    INR: { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
    AUD: { symbol: 'A$', code: 'AUD', name: 'Australian Dollar' },
    CAD: { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar' },
    CHF: { symbol: 'CHF', code: 'CHF', name: 'Swiss Franc' },
    SEK: { symbol: 'kr', code: 'SEK', name: 'Swedish Krona' },
    NOK: { symbol: 'kr', code: 'NOK', name: 'Norwegian Krone' },
    DKK: { symbol: 'kr', code: 'DKK', name: 'Danish Krone' },
    PLN: { symbol: 'zł', code: 'PLN', name: 'Polish Zloty' },
    CZK: { symbol: 'Kč', code: 'CZK', name: 'Czech Koruna' },
    HUF: { symbol: 'Ft', code: 'HUF', name: 'Hungarian Forint' },
    RON: { symbol: 'lei', code: 'RON', name: 'Romanian Leu' },
    BGN: { symbol: 'лв', code: 'BGN', name: 'Bulgarian Lev' },
    HRK: { symbol: 'kn', code: 'HRK', name: 'Croatian Kuna' },
    RUB: { symbol: '₽', code: 'RUB', name: 'Russian Ruble' },
    TRY: { symbol: '₺', code: 'TRY', name: 'Turkish Lira' },
    BRL: { symbol: 'R$', code: 'BRL', name: 'Brazilian Real' },
    MXN: { symbol: '$', code: 'MXN', name: 'Mexican Peso' },
    ARS: { symbol: '$', code: 'ARS', name: 'Argentine Peso' },
    CLP: { symbol: '$', code: 'CLP', name: 'Chilean Peso' },
    COP: { symbol: '$', code: 'COP', name: 'Colombian Peso' },
    PEN: { symbol: 'S/', code: 'PEN', name: 'Peruvian Sol' },
    ZAR: { symbol: 'R', code: 'ZAR', name: 'South African Rand' },
    NZD: { symbol: 'NZ$', code: 'NZD', name: 'New Zealand Dollar' },
    SGD: { symbol: 'S$', code: 'SGD', name: 'Singapore Dollar' },
    HKD: { symbol: 'HK$', code: 'HKD', name: 'Hong Kong Dollar' },
    KRW: { symbol: '₩', code: 'KRW', name: 'South Korean Won' },
    THB: { symbol: '฿', code: 'THB', name: 'Thai Baht' },
    MYR: { symbol: 'RM', code: 'MYR', name: 'Malaysian Ringgit' },
    PHP: { symbol: '₱', code: 'PHP', name: 'Philippine Peso' },
    IDR: { symbol: 'Rp', code: 'IDR', name: 'Indonesian Rupiah' },
    VND: { symbol: '₫', code: 'VND', name: 'Vietnamese Dong' },
    ILS: { symbol: '₪', code: 'ILS', name: 'Israeli New Shekel' },
    AED: { symbol: 'د.إ', code: 'AED', name: 'UAE Dirham' },
    SAR: { symbol: '﷼', code: 'SAR', name: 'Saudi Riyal' },
    KWD: { symbol: 'د.ك', code: 'KWD', name: 'Kuwaiti Dinar' },
    QAR: { symbol: 'ر.ق', code: 'QAR', name: 'Qatari Riyal' },
    BHD: { symbol: 'ب.د', code: 'BHD', name: 'Bahraini Dinar' },
    OMR: { symbol: 'ر.ع', code: 'OMR', name: 'Omani Rial' },
    JOD: { symbol: 'د.ا', code: 'JOD', name: 'Jordanian Dinar' },
    LBP: { symbol: 'ل.ل', code: 'LBP', name: 'Lebanese Pound' },
    EGP: { symbol: 'ج.م', code: 'EGP', name: 'Egyptian Pound' },
    MAD: { symbol: 'د.م', code: 'MAD', name: 'Moroccan Dirham' },
    TND: { symbol: 'د.ت', code: 'TND', name: 'Tunisian Dinar' },
    DZD: { symbol: 'د.ج', code: 'DZD', name: 'Algerian Dinar' },
    NGN: { symbol: '₦', code: 'NGN', name: 'Nigerian Naira' },
    KES: { symbol: 'KSh', code: 'KES', name: 'Kenyan Shilling' },
    GHS: { symbol: '₵', code: 'GHS', name: 'Ghanaian Cedi' },
    UGX: { symbol: 'USh', code: 'UGX', name: 'Ugandan Shilling' },
    TZS: { symbol: 'TSh', code: 'TZS', name: 'Tanzanian Shilling' },
    RWF: { symbol: 'RFw', code: 'RWF', name: 'Rwandan Franc' },
    BIF: { symbol: 'FBu', code: 'BIF', name: 'Burundian Franc' },
    DJF: { symbol: 'Fdj', code: 'DJF', name: 'Djiboutian Franc' },
    ETB: { symbol: 'Br', code: 'ETB', name: 'Ethiopian Birr' },
    SDG: { symbol: 'ج.س', code: 'SDG', name: 'Sudanese Pound' },
    LYD: { symbol: 'د.ل', code: 'LYD', name: 'Libyan Dinar' },
    TMT: { symbol: 'm', code: 'TMT', name: 'Turkmenistani Manat' },
    AZN: { symbol: '₼', code: 'AZN', name: 'Azerbaijani Manat' },
    GEL: { symbol: '₾', code: 'GEL', name: 'Georgian Lari' },
    AMD: { symbol: '֏', code: 'AMD', name: 'Armenian Dram' },
    KZT: { symbol: '₸', code: 'KZT', name: 'Kazakhstani Tenge' },
    KGS: { symbol: 'лв', code: 'KGS', name: 'Kyrgyzstani Som' },
    UZS: { symbol: 'лв', code: 'UZS', name: 'Uzbekistan Som' },
    TJS: { symbol: 'SM', code: 'TJS', name: 'Tajikistani Somoni' },
    AFN: { symbol: '؋', code: 'AFN', name: 'Afghan Afghani' },
    PKR: { symbol: '₨', code: 'PKR', name: 'Pakistani Rupee' },
    LKR: { symbol: 'රු', code: 'LKR', name: 'Sri Lankan Rupee' },
    MVR: { symbol: 'Rf', code: 'MVR', name: 'Maldivian Rufiyaa' },
    NPR: { symbol: '₨', code: 'NPR', name: 'Nepalese Rupee' },
    BTN: { symbol: 'Nu.', code: 'BTN', name: 'Bhutanese Ngultrum' },
    BDT: { symbol: '৳', code: 'BDT', name: 'Bangladeshi Taka' },
    MMK: { symbol: 'K', code: 'MMK', name: 'Myanmar Kyat' },
    LAK: { symbol: '₭', code: 'LAK', name: 'Lao Kip' },
    KHR: { symbol: '៛', code: 'KHR', name: 'Cambodian Riel' },
    MOP: { symbol: 'MOP$', code: 'MOP', name: 'Macanese Pataca' },
    NAD: { symbol: 'N$', code: 'NAD', name: 'Namibian Dollar' },
    BWP: { symbol: 'P', code: 'BWP', name: 'Botswana Pula' },
    LSL: { symbol: 'L', code: 'LSL', name: 'Lesotho Loti' },
    SZL: { symbol: 'E', code: 'SZL', name: 'Swazi Lilangeni' },
    MWK: { symbol: 'MK', code: 'MWK', name: 'Malawian Kwacha' },
    ZMW: { symbol: 'ZK', code: 'ZMW', name: 'Zambian Kwacha' },
    BZD: { symbol: 'BZ$', code: 'BZD', name: 'Belize Dollar' },
    GTQ: { symbol: 'Q', code: 'GTQ', name: 'Guatemalan Quetzal' },
    HNL: { symbol: 'L', code: 'HNL', name: 'Honduran Lempira' },
    NIO: { symbol: 'C$', code: 'NIO', name: 'Nicaraguan Córdoba' },
    CRC: { symbol: '₡', code: 'CRC', name: 'Costa Rican Colón' },
    PAB: { symbol: 'B/.', code: 'PAB', name: 'Panamanian Balboa' },
    DOP: { symbol: 'RD$', code: 'DOP', name: 'Dominican Peso' },
    HTG: { symbol: 'G', code: 'HTG', name: 'Haitian Gourde' },
    XCD: { symbol: '$', code: 'XCD', name: 'East Caribbean Dollar' },
    TTD: { symbol: 'TT$', code: 'TTD', name: 'Trinidad and Tobago Dollar' },
    BBD: { symbol: 'Bds$', code: 'BBD', name: 'Barbadian Dollar' },
    JMD: { symbol: 'J$', code: 'JMD', name: 'Jamaican Dollar' },
    BSD: { symbol: 'B$', code: 'BSD', name: 'Bahamian Dollar' },
    KYD: { symbol: 'CI$', code: 'KYD', name: 'Cayman Islands Dollar' },
    FJD: { symbol: 'F$', code: 'FJD', name: 'Fijian Dollar' },
    SBD: { symbol: 'SI$', code: 'SBD', name: 'Solomon Islands Dollar' },
    VUV: { symbol: 'VT', code: 'VUV', name: 'Vanuatu Vatu' },
    WST: { symbol: 'WS$', code: 'WST', name: 'Samoan Tālā' },
    TOP: { symbol: 'T$', code: 'TOP', name: 'Tongan Paʻanga' },
    PGK: { symbol: 'K', code: 'PGK', name: 'Papua New Guinean Kina' },
    SCR: { symbol: '₨', code: 'SCR', name: 'Seychellois Rupee' },
    MUR: { symbol: '₨', code: 'MUR', name: 'Mauritian Rupee' },
    COM: { symbol: 'CF', code: 'COM', name: 'Comorian Franc' },
    KMF: { symbol: 'CF', code: 'KMF', name: 'Comorian Franc' },
    YER: { symbol: '﷼', code: 'YER', name: 'Yemeni Rial' },
    IQD: { symbol: 'ع.د', code: 'IQD', name: 'Iraqi Dinar' },
    SYP: { symbol: '£S', code: 'SYP', name: 'Syrian Pound' },
    GNF: { symbol: 'FG', code: 'GNF', name: 'Guinean Franc' },
    CDF: { symbol: 'FC', code: 'CDF', name: 'Congolese Franc' },
    AOA: { symbol: 'Kz', code: 'AOA', name: 'Angolan Kwanza' },
    XOF: { symbol: 'CFA', code: 'XOF', name: 'West African CFA Franc' },
    XAF: { symbol: 'FCFA', code: 'XAF', name: 'Central African CFA Franc' },
    XPF: { symbol: 'CFP', code: 'XPF', name: 'CFP Franc' },
    VEF: { symbol: 'Bs', code: 'VEF', name: 'Venezuelan Bolívar' },
    BOB: { symbol: 'Bs', code: 'BOB', name: 'Bolivian Boliviano' },
    PYG: { symbol: '₲', code: 'PYG', name: 'Paraguayan Guaraní' },
    UYU: { symbol: '$U', code: 'UYU', name: 'Uruguayan Peso' },
    GYD: { symbol: 'G$', code: 'GYD', name: 'Guyanaese Dollar' },
    SRD: { symbol: '$', code: 'SRD', name: 'Surinamese Dollar' },
    AWG: { symbol: 'ƒ', code: 'AWG', name: 'Aruban Florin' },
    ANG: { symbol: 'ƒ', code: 'ANG', name: 'Netherlands Antillean Guilder' },
    CUP: { symbol: '₱', code: 'CUP', name: 'Cuban Peso' },
    SVC: { symbol: '₡', code: 'SVC', name: 'Salvadoran Colón' },
    BYN: { symbol: 'Br', code: 'BYN', name: 'Belarusian Ruble' },
    ISK: { symbol: 'kr', code: 'ISK', name: 'Icelandic Króna' },
    TWD: { symbol: 'NT$', code: 'TWD', name: 'New Taiwan Dollar' }
};

/**
 * Live FX rates, USD-anchored: "how many units of currency X equal 1 USD".
 *
 * Rates are deliberately NOT hardcoded here - they are fetched from the backend
 * (GET /currency/rates, which caches live provider rates for 12h with a
 * fallback), so they never go stale. Until rates arrive, formatCurrency* simply
 * prints the store's own amounts with its own symbol - which is exactly correct,
 * because API data already comes back in the store's currency. Conversion only
 * runs when an explicit targetCurrency is requested.
 */
const LIVE_RATES: Record<string, number> = { USD: 1 };
let ratesLoaded = false;
let ratesRequested = false;

/** Fetch live USD-anchored rates once (idempotent, safe to call on boot). */
export async function loadExchangeRates(): Promise<void> {
    if (ratesRequested) return;
    ratesRequested = true;
    try {
        const res = await axios.get('/currency/rates');
        const rates = res?.data?.rates;
        if (rates && typeof rates === 'object') {
            Object.assign(LIVE_RATES, rates);
            ratesLoaded = true;
        }
    } catch {
        // Network/provider failure: keep store-currency display (no conversion).
    }
}

/** Whether live rates have been successfully loaded. */
export function hasLiveRates(): boolean {
    return ratesLoaded;
}

/**
 * Converts a value from one currency to another using live USD-anchored rates.
 *
 * Resolution:
 *   1. X -> USD -> Y using the live table.
 *   2. If either rate is unknown, returns the value unchanged so callers never
 *      crash and never display a fabricated number.
 */
export function convertCurrency(value: number, from: string, to: string): number {
    if (!from || !to) return value;
    const f = from.split(' ')[0].toUpperCase();
    const t = to.split(' ')[0].toUpperCase();
    if (f === t) return value;

    const toUsd = f === 'USD' ? 1 : (LIVE_RATES[f] ? 1 / LIVE_RATES[f] : undefined);
    const fromUsd = t === 'USD' ? 1 : LIVE_RATES[t];
    if (toUsd !== undefined && fromUsd !== undefined) {
        return value * toUsd * fromUsd;
    }

    // Unknown currency pair - leave the numeric value untouched.
    return value;
}

/**
 * Get currency symbol from currency code
 */
export function getCurrencySymbol(currencyCode: string): string {
    if (!currencyCode) return '$';
    const cleanCode = currencyCode.split(' ')[0].toUpperCase();
    return CURRENCY_MAP[cleanCode]?.symbol || currencyCode;
}

/**
 * Get currency information from currency code
 */
export function getCurrencyInfo(currencyCode: string): CurrencyInfo {
    if (!currencyCode) return CURRENCY_MAP.USD;
    const cleanCode = currencyCode.split(' ')[0].toUpperCase();
    return CURRENCY_MAP[cleanCode] || CURRENCY_MAP.USD;
}

/**
 * Get the current store currency from auth context
 */
export function getCurrentStoreCurrency(
    stores: Array<{ shop: string; currency?: string }>, 
    currentShop: string | null
): string {
    if (!currentShop || !stores.length) return 'INR';
    const currentStore = stores.find(store => store.shop === currentShop);
    return currentStore?.currency || 'INR';
}

/**
 * Map currency code to BCP 47 locale
 */
export const CURRENCY_TO_LOCALE: Record<string, string> = {
    INR: 'en-IN',
    USD: 'en-US',
    GBP: 'en-GB',
    EUR: 'de-DE',
    AUD: 'en-AU',
    CAD: 'en-CA',
    NZD: 'en-NZ',
    SGD: 'en-SG',
    HKD: 'en-HK',
    JPY: 'ja-JP',
    CNY: 'zh-CN',
    KRW: 'ko-KR',
    THB: 'th-TH',
    MYR: 'ms-MY',
    IDR: 'id-ID',
    PHP: 'en-PH',
    VND: 'vi-VN',
    PKR: 'en-PK',
    BDT: 'bn-BD',
    LKR: 'si-LK',
    AED: 'ar-AE',
    SAR: 'ar-SA',
    ZAR: 'en-ZA',
    BRL: 'pt-BR',
    MXN: 'es-MX',
    ARS: 'es-AR',
    CLP: 'es-CL',
    COP: 'es-CO',
    CHF: 'de-CH',
    SEK: 'sv-SE',
    NOK: 'nb-NO',
    DKK: 'da-DK',
    PLN: 'pl-PL',
    CZK: 'cs-CZ',
    HUF: 'hu-HU',
    TRY: 'tr-TR',
    RUB: 'ru-RU',
};

export function getLocaleFromCurrency(currencyCode: string): string {
    if (!currencyCode) return 'en-US';
    const normalized = currencyCode.split(' ')[0].toUpperCase();
    return CURRENCY_TO_LOCALE[normalized] ?? 'en-US';
}

export interface FormatCurrencyOptions {
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    compact?: boolean;
    fallback?: string;
    targetCurrency?: string;
}

/**
 * Format a value as currency using shop currency.
 */
export function formatCurrencyValue(
    value: number | string | null | undefined,
    currencyCode: string,
    options: FormatCurrencyOptions = {}
): string {
    const {
        maximumFractionDigits = 0,
        minimumFractionDigits,
        compact = false,
        fallback = '-',
        targetCurrency,
    } = options;

    let numeric = typeof value === 'string' ? Number(value) : value;
    if (numeric == null || Number.isNaN(numeric)) return fallback;

    const cleanSourceCode = currencyCode.split(' ')[0].toUpperCase();
    const cleanTargetCode = (targetCurrency || currencyCode).split(' ')[0].toUpperCase();
    
    if (cleanTargetCode !== cleanSourceCode) {
        numeric = convertCurrency(numeric, cleanSourceCode, cleanTargetCode);
    }

    const locale = getLocaleFromCurrency(cleanTargetCode);
    const symbol = getCurrencySymbol(cleanTargetCode);
    const absValue = Math.abs(numeric);
    const sign = numeric < 0 ? '-' : '';

    if (compact) {
        let formatted: string;
        if (absValue >= 1_00_00_000 && locale === 'en-IN') {
             formatted = (absValue / 1_00_00_000).toFixed(1) + ' Cr';
        } else if (absValue >= 1_00_000 && locale === 'en-IN') {
             formatted = (absValue / 1_00_000).toFixed(1) + ' L';
        } else if (absValue >= 1_000_000_000) {
            formatted = (absValue / 1_000_000_000).toFixed(1) + 'B';
        } else if (absValue >= 1_000_000) {
            formatted = (absValue / 1_000_000).toFixed(1) + 'M';
        } else if (absValue >= 1_000) {
            formatted = (absValue / 1_000).toFixed(0) + 'k';
        } else {
            formatted = absValue.toLocaleString(locale, { maximumFractionDigits: 0 });
        }
        return `${sign}${symbol}${formatted}`;
    }

    try {
        return numeric.toLocaleString(locale, {
            style: 'currency',
            currency: cleanTargetCode,
            maximumFractionDigits,
            minimumFractionDigits,
        });
    } catch {
        // Invalid/unsupported ISO currency code - fall back to symbol + number
        // so an unknown store currency can never crash a widget.
        const plain = numeric.toLocaleString(locale, { maximumFractionDigits, minimumFractionDigits });
        return `${symbol}${plain}`;
    }
}

/**
 * Format a value as currency with symbol prefix
 */
export function formatCurrencyWithSymbol(
    value: number | string | null | undefined,
    currencyCode: string,
    options: FormatCurrencyOptions = {}
): string {
    const {
        maximumFractionDigits = 0,
        minimumFractionDigits,
        fallback = '-',
        targetCurrency,
    } = options;

    let numeric = typeof value === 'string' ? Number(value) : value;
    if (numeric == null || Number.isNaN(numeric)) return fallback;

    const cleanSourceCode = currencyCode.split(' ')[0].toUpperCase();
    const cleanTargetCode = (targetCurrency || currencyCode).split(' ')[0].toUpperCase();
    
    if (cleanTargetCode !== cleanSourceCode) {
        numeric = convertCurrency(numeric, cleanSourceCode, cleanTargetCode);
    }

    const locale = getLocaleFromCurrency(cleanTargetCode);
    const symbol = getCurrencySymbol(cleanTargetCode);
    const formatted = numeric.toLocaleString(locale, {
        maximumFractionDigits,
        minimumFractionDigits,
    });
    return `${symbol}${formatted}`;
}

export interface FormatNumberOptions {
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    /** When true, format with exactly 2 decimal places (e.g. for Y-axis labels) */
    axis?: boolean;
    /** Compact form: 1.2M, 10K (for charts) */
    compact?: boolean;
    fallback?: string;
}

/**
 * Format a number by locale
 */
export function formatNumberValue(
    value: number | string | null | undefined,
    locale: string,
    options: FormatNumberOptions = {}
): string {
    const {
        maximumFractionDigits,
        minimumFractionDigits,
        axis = false,
        fallback = '-',
    } = options;

    const numeric = typeof value === 'string' ? Number(value) : value;
    if (numeric == null || Number.isNaN(numeric)) return fallback;

    const absValue = Math.abs(numeric);
    const sign = numeric < 0 ? '-' : '';

    if (options.compact) {
        let formatted: string;
        if (absValue >= 1_000_000_000) {
            formatted = (absValue / 1_000_000_000).toFixed(1) + 'B';
        } else if (absValue >= 1_000_000) {
            formatted = (absValue / 1_000_000).toFixed(1) + 'M';
        } else if (absValue >= 1_000) {
            formatted = (absValue / 1_000).toFixed(1) + 'K';
        } else {
            formatted = absValue.toLocaleString(locale, { maximumFractionDigits: 0 });
        }
        return `${sign}${formatted}`;
    }

    const opts: Intl.NumberFormatOptions = {};
    if (axis) {
        opts.minimumFractionDigits = 2;
        opts.maximumFractionDigits = 2;
    } else {
        if (maximumFractionDigits !== undefined) opts.maximumFractionDigits = maximumFractionDigits;
        if (minimumFractionDigits !== undefined) opts.minimumFractionDigits = minimumFractionDigits;
    }
    return numeric.toLocaleString(locale, opts);
}
